// ─────────────────────────────────────────────────────────────────────────────
// Language detection utility
// Pure functions, no React, no side effects beyond an optional localStorage
// read. Used by RootLanguageRedirect before any React state is available.
//
// Priority chain (matches i18next's detector for consistency):
//   1. /:lang/ prefix in the current URL
//   2. ?lang=xx query parameter
//   3. Previously stored choice in localStorage
//   4. Operating-system/browser locale from navigator language fields
//   5. Browser timezone / region fallback
//   6. Default language ("en")
// ─────────────────────────────────────────────────────────────────────────────

import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/lib/languages";

const STORAGE_KEYS = ["appLanguage", "marstick_locale", "i18nextLng"];

function normalize(code) {
  if (!code) return null;
  const base = String(code).toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : null;
}

export function detectFromPath(pathname = window.location.pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  return normalize(seg);
}

export function detectFromQuery(search = window.location.search) {
  try {
    const lng = new URLSearchParams(search).get("lang");
    return normalize(lng);
  } catch {
    return null;
  }
}

export function detectFromStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    if (window.localStorage.getItem("marstick_locale_explicit") !== "true") return null;
    for (const key of STORAGE_KEYS) {
      const code = normalize(window.localStorage.getItem(key));
      if (code) return code;
    }
    return null;
  } catch {
    return null;
  }
}

export function detectFromBrowser() {
  if (typeof navigator === "undefined") return null;
  const languageFields = [
    navigator.language,
    navigator.userLanguage,
    navigator.systemLanguage,
    navigator.browserLanguage,
  ].filter(Boolean);
  const list = [
    ...languageFields,
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
  ];
  for (const candidate of list) {
    const code = normalize(candidate);
    if (code) return code;
  }
  return null;
}

const TIMEZONE_LANGUAGE_HINTS = {
  "Asia/Jerusalem": "he",
  "Europe/Madrid": "es",
  "Atlantic/Canary": "es",
  "Europe/Rome": "it",
  "Europe/Paris": "fr",
  "Europe/Berlin": "de",
  "Europe/Vienna": "de",
  "Europe/Zurich": "de",
  "Europe/Lisbon": "pt",
  "Atlantic/Azores": "pt",
  "America/Sao_Paulo": "pt",
  "Asia/Dubai": "ar",
  "Asia/Riyadh": "ar",
  "Africa/Cairo": "ar",
  "Europe/Athens": "el",
  "Asia/Nicosia": "el",
  "Europe/Moscow": "ru",
  "Europe/Istanbul": "tr",
  "Asia/Shanghai": "zh",
  "Asia/Hong_Kong": "zh",
  "Asia/Taipei": "zh",
};

export function detectFromTimezone() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return normalize(TIMEZONE_LANGUAGE_HINTS[timezone]);
  } catch {
    return null;
  }
}

// Main entry point. Always returns a supported code.
export function detectLanguage({
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
  search = typeof window !== "undefined" ? window.location.search : "",
} = {}) {
  return (
    detectFromPath(pathname) ||
    detectFromQuery(search) ||
    detectFromStorage() ||
    detectFromBrowser() ||
    detectFromTimezone() ||
    DEFAULT_LANGUAGE
  );
}

// Persist the user's explicit choice so future visits skip detection.
export function persistLanguageChoice(lang) {
  try {
    if (
      typeof window === "undefined" ||
      !window.localStorage ||
      !SUPPORTED_LANGUAGES.includes(lang)
    ) return;
    window.localStorage.setItem("appLanguage", lang);
    window.localStorage.setItem("marstick_locale", lang);
    window.localStorage.setItem("i18nextLng", lang);
    window.localStorage.setItem("marstick_locale_explicit", "true");
    window.localStorage.removeItem("lang");
    window.localStorage.removeItem("language");
    window.localStorage.removeItem("locale");
  } catch {
    // ignore (private mode, quota exceeded, etc.)
  }
}